import React, {
  Dispatch, lazy, SetStateAction, Suspense,
} from 'react';
import {
  Button, Gallery, Grid, Heading, Modal, RichText, Spinner,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { Link as GatsbyLink } from 'gatsby';
import axios from 'axios';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { ReviewStars } from '../../review-stars/ReviewStars';
import { buildContenfulBaseURL } from '../../../services/home/HomePageService';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import type { QuickViewModalState } from '../ProductsSection';

const Markdown = lazy(() => import('@printdeal/markdown/react').then((module) => ({ default: module.Markdown })));

interface Props {
  setQuickViewModal: Dispatch<SetStateAction<QuickViewModalState | null>>
  quickViewModal: QuickViewModalState,
}

type ProductContent = {
  title: string;
  subtitle?: string;
  description?: string;
  gallery?: {
    imagesCollection: {
      items: {
        url: string;
        title: string;
      }[];
    };
  };
};

type GetProductPageResponse = {
  productContent: ProductContent;
};

const messages = {
  prev: { id: 'general.pagination.button.previous', defaultMessage: 'Previous' },
  next: { id: 'general.pagination.button.previous', defaultMessage: 'Next' },
};

const query = `
  query getProductPage($contentfulId: String!) {
    productContent(id: $contentfulId) {
      title
      subtitle
      description
      gallery {
        ... on Gallery {
          imagesCollection {
            items {
              url
              title
            }
          }
        }
      }
    }
  }
`;

const baseURL = buildContenfulBaseURL(
  process.env.GATSBY_CONTENTFUL_GRAPHQL_API_BASE_URL as string,
  process.env.GATSBY_CONTENTFUL_SPACE_ID as string,
  process.env.GATSBY_CONTENTFUL_ENVIRONMENT as string,
);

// Initialize GraphQLClient with headers
const client = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${process.env.GATSBY_CONTENTFUL_ACCESS_TOKEN}`,
  },
});

export const QuickViewModal: React.FC<Props> = ({ quickViewModal, setQuickViewModal }) => {
  const { contentfulId, reviews, path } = quickViewModal || {};
  const intl = useIntl();
  const { data, status } = useQuery<GetProductPageResponse>({
    queryKey: ['productContent', contentfulId],
    queryFn: async () => {
      const variables = { contentfulId };
      const { data: graphQLResponse } = await client.post('/', {
        query,
        variables,
      });

      return graphQLResponse.data;
    },
    enabled: Boolean(contentfulId), // Ensures the query runs only if contentfulId is truthy
  });

  const { prev, next } = messages;
  const product = data?.productContent;
  const {
    title, subtitle, description, gallery,
  } = product || {};
  const images = gallery?.imagesCollection?.items || [];

  const sendClickEvent = () => {
    dispatchDataLayerEntry(
      'click_on_show_product_page_button',
      'click',
      {
        contentful_id: contentfulId,
        title,
      },
    );
  };

  const handleClose = (closeType: 'crossIcon' | 'closeButton') => {
    setQuickViewModal(null);

    // Dispatch data layer event with the appropriate event action
    addDataLayerEntry('close_quick_view_modal', {
      experimentKey: 'EXP1432',
      contentful_id: contentfulId,
      handleCloseType: closeType,
      eventNonInteraction: false,
      title,
    });
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'quick.view.model.title', defaultMessage: 'Product information' })}
      size="xl"
      dismissLabel="Dismiss"
      isOpen={Boolean(quickViewModal)}
      onClose={() => handleClose('crossIcon')}
    >
      {status === 'pending' && (
        <div className="pd-flex pd-h-96 pd-items-center pd-justify-center">
          <Spinner
            variant="primary"
            screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            size="xl"
          />
        </div>
      )}
      {status === 'error' && (
        <div className="pd-flex pd-h-96 pd-items-center pd-justify-center">
          <FormattedMessage
            id="quick.view.modal.error"
            defaultMessage="An error occurred while loading the product information."
          />
        </div>
      )}
      {status === 'success' && (
        <>
          <Grid className="pd-grid-cols-12 pd-gap-6 pd-p-6">
            <Grid.Item className="pd-col-span-5">
              <Gallery
                id="quick-view-gallery"
                prevButton={<Gallery.Button variant="prev" screenReaderLabel={intl.formatMessage(prev)} />}
                nextButton={<Gallery.Button variant="next" screenReaderLabel={intl.formatMessage(next)} />}
              >
                <Gallery.Root>
                  {images.map(({ url, title: alt }) => (
                    <Gallery.Item key={url}>
                      <img src={url} alt={alt} className="pd-cover-parent" />
                    </Gallery.Item>
                  ))}
                </Gallery.Root>
                <Gallery.Navigation maxItems={3} className="pd-mt-6">
                  {images.map(({ url, title: alt }) => (
                    <Gallery.Navigation.Item key={url}>
                      <img src={url} alt={alt} className="pd-h-20 pd-w-full" />
                    </Gallery.Navigation.Item>
                  ))}
                </Gallery.Navigation>
              </Gallery>
            </Grid.Item>
            <Grid.Item className="pd-col-span-7">
              <Heading as="h1" size="1">{title}</Heading>
              <Heading variant="display" as="h2" size="3" className="pd-mt-2 pd-text-dim-800">{subtitle}</Heading>
              {reviews && (
                <div className="pd-mt-3 pd-flex pd-items-center">
                  <ReviewStars className="pd-mr-2" score={reviews.score} />
                  <FormattedMessage
                    id="productDetail.reviews.total"
                    defaultMessage="{total} Reviews"
                    values={{ total: reviews.totalReviews }}
                  />
                </div>
              )}
              <p className="pd-mt-4 pd-line-clamp-[12] [&_h2]:pd-text-md [&_h3]:pd-text-sm">
                <Suspense>
                  <RichText as={Markdown}>
                    {description}
                  </RichText>
                </Suspense>
              </p>
            </Grid.Item>
          </Grid>
          <Modal.Footer className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2" data-testid="footer-modal-footer">
            <Button
              as={GatsbyLink}
              to={path}
              onClick={sendClickEvent}
              variant="primary"
              size="md"
            >
              <FormattedMessage id="quick.view.model.button.pdp" defaultMessage="Show product" />
            </Button>
            <Button variant="secondary" onClick={() => handleClose('closeButton')}>
              <FormattedMessage id="generic.modal.close" defaultMessage="Close" />
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};
